import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const BRANDS = ["BMW", "Mercedes-Benz", "Audi", "Porsche", "Tesla", "Lexus", "Acura", "Genesis", "Cadillac", "Toyota", "Honda", "Ford", "Chevrolet", "Volvo", "Other"];

export default function FindMyCar() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    make: "", model: "", budget: "1000",
    deal_type: "lease", down_payment: "3000",
    trade_in: "no", timeline: "1_month",
    color: "", features: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { type: "find_my_car", payload: form });
      setDone(true);
      toast.success("Request sent! A broker will reach out within 24 hours.");
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[700px] mx-auto px-5 py-24 text-center" data-testid="find-my-car-success">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="font-display text-4xl font-light tracking-tighter mb-3">We're on it.</h2>
        <p className="text-gray-400 mb-8">A 555 broker will reach out within 24 hours with sourcing options that match your brief.</p>
        <a href="/lease-deals" className="px-6 py-3 rounded-full border border-white/20 hover:bg-white/5">Browse Current Deals</a>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="find-my-car-page">
      <p className="label-mono mb-3">Concierge Sourcing</p>
      <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-5">Find me this car.</h1>
      <p className="text-gray-400 max-w-2xl mb-12">Tell us exactly what you want — make, model, payment target, trade situation. A real broker (backed by AI research) builds you options within 24 hours.</p>

      <form onSubmit={submit} className="grid md:grid-cols-2 gap-x-8 gap-y-6 glass rounded-2xl p-7 md:p-10">
        <div className="md:col-span-2 label-mono">About You</div>
        <Input required placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="fmc-name" />
        <Input required placeholder="Email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="fmc-email" />
        <Input required placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white md:col-span-2" data-testid="fmc-phone" />

        <div className="md:col-span-2 label-mono mt-2">Vehicle</div>
        <select required value={form.make} onChange={(e) => set("make", e.target.value)} className="bg-[#0A0A0E] border border-[#1A1A24] h-12 rounded-xl text-white px-3 text-sm" data-testid="fmc-make">
          <option value="">Select brand…</option>
          {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <Input placeholder="Model (e.g. X5, Model Y)" value={form.model} onChange={(e) => set("model", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="fmc-model" />
        <Input placeholder="Preferred color" value={form.color} onChange={(e) => set("color", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="fmc-color" />
        <Input placeholder="Must-have features" value={form.features} onChange={(e) => set("features", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="fmc-features" />

        <div className="md:col-span-2 label-mono mt-2">Budget & Terms</div>
        <Input placeholder="Target monthly payment ($)" value={form.budget} onChange={(e) => set("budget", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="fmc-budget" />
        <Input placeholder="Down payment ($)" value={form.down_payment} onChange={(e) => set("down_payment", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="fmc-down" />
        <select value={form.deal_type} onChange={(e) => set("deal_type", e.target.value)} className="bg-[#0A0A0E] border border-[#1A1A24] h-12 rounded-xl text-white px-3 text-sm">
          <option value="lease">Lease</option>
          <option value="finance">Finance</option>
          <option value="cash">Cash</option>
        </select>
        <select value={form.trade_in} onChange={(e) => set("trade_in", e.target.value)} className="bg-[#0A0A0E] border border-[#1A1A24] h-12 rounded-xl text-white px-3 text-sm">
          <option value="no">No trade-in</option>
          <option value="yes">Yes, I have a trade-in</option>
        </select>
        <select value={form.timeline} onChange={(e) => set("timeline", e.target.value)} className="bg-[#0A0A0E] border border-[#1A1A24] h-12 rounded-xl text-white px-3 text-sm md:col-span-2">
          <option value="asap">ASAP — within a week</option>
          <option value="1_month">Within a month</option>
          <option value="3_months">1-3 months</option>
          <option value="exploring">Just exploring</option>
        </select>

        <div className="md:col-span-2">
          <button type="submit" disabled={submitting} className="w-full md:w-auto px-10 py-4 rounded-full silver-gradient text-[#050507] font-bold tracking-wide disabled:opacity-50" data-testid="fmc-submit">
            {submitting ? "Sending…" : "Send My Request"}
          </button>
        </div>
      </form>
    </div>
  );
}
